import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso';
import { motion, AnimatePresence } from 'motion/react';
import {
  Archive, CheckCheck, Flag, Inbox, Layers, ListChecks, Mail, MailOpen, Paperclip, PencilLine, RefreshCw, ShieldAlert, Sparkles, Trash2, X,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { displayName, groupConversations, parseAddressList, parseMailbox, type Conversation, type MessageSummary } from '@dts/core';
import { useUi, type MailFilter, type View } from '@/lib/store';
import { isUnreadEverywhere, patchCachedMessages, qk, UNREAD_EXCLUDED, useFolders, useMailActions, useMessages, useMyAddresses, usePrefs } from '@/lib/queries';
import { api, errorMessage } from '@/lib/api';
import { CATEGORY_META, listTime } from '@/lib/format';
import { Avatar } from './Avatar';
import { JunkScanDialog } from './JunkScan';
import { DeleteFolderDialog, folderLeaf, renameFolder } from './FolderActions';
import { askText, confirmDialog } from './Dialogs';
import { Button, Chip, cn, Empty, IconButton, Num, Spinner, tint } from './ui';

type MailView = Extract<View, { kind: 'mail' }>;

const FILTERS: { id: MailFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'unread', label: 'Unread' },
  { id: 'needs_reply', label: 'Needs reply' },
  { id: 'important', label: 'Important' },
  { id: 'flagged', label: 'Flagged' },
];

function senderLabel(c: Conversation, mine: string[]): string {
  const names = c.messages
    .map((m) => (mine.includes(m.fromAddress.toLowerCase()) ? 'me' : displayName(parseMailbox(m.from) ?? { address: m.fromAddress }).split(' ')[0]))
    .filter((n, i, a) => a.indexOf(n) === i)
    .reverse();
  if (c.messages.length === 1) {
    const m = c.latest;
    if (m.direction === 'outbound' || m.folder === 'Sent' || m.folder === 'Drafts') {
      const to = parseAddressList(m.to)[0];
      return to ? `To: ${displayName(to)}` : '(no recipients)';
    }
    return displayName(parseMailbox(m.from) ?? { address: m.fromAddress });
  }
  return names.slice(-3).join(', ');
}

/** Auto-triage: ask the AI to categorize visible messages that have no category yet (batched, once per id). */
const triaged = new Set<string>();
function useAutoTriage(items: MessageSummary[], enabled: boolean) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!enabled) return;
    const todo = items.filter((m) => !m.aiCategory && !triaged.has(m.id) && m.folder !== 'Drafts' && m.folder !== 'Junk' && m.folder !== 'Trash' && m.direction !== 'outbound').slice(0, 20);
    if (todo.length === 0) return;
    todo.forEach((m) => triaged.add(m.id));
    const t = setTimeout(() => {
      api.triage(todo.map((m) => m.id))
        .then((results) => {
          const byId = new Map(results.map((r) => [r.id, r]));
          patchCachedMessages(qc, [...byId.keys()], (m) => ({ ...m, ...byId.get(m.id)! }));
        })
        .catch(() => todo.forEach((m) => triaged.delete(m.id)));
    }, 400);
    return () => clearTimeout(t);
  }, [items, enabled, qc]);
}

export function MessageList({ view }: { view: MailView }) {
  const q = useMessages(view);
  const mine = useMyAddresses();
  const prefs = usePrefs().data;
  const local = useUi((s) => s.local);
  const setLocal = useUi((s) => s.setLocal);
  const setView = useUi((s) => s.setView);
  const selectedId = useUi((s) => s.selectedId);
  const select = useUi((s) => s.select);
  const selection = useUi((s) => s.selection);
  const setSelection = useUi((s) => s.setSelection);
  const actions = useMailActions();
  const virtuoso = useRef<VirtuosoHandle>(null);

  const items = useMemo(() => q.data?.pages.flatMap((p) => p.items) ?? [], [q.data]);
  useAutoTriage(items, prefs?.ai?.autoTriage !== false);

  const filtered = useMemo(() => {
    const f = view.filter ?? 'all';
    // A server without the exclude filter still returns Junk and Trash here.
    let list = isUnreadEverywhere(view) ? items.filter((m) => !UNREAD_EXCLUDED.includes(m.folder)) : items;
    // Views spanning folders would show mail you sent to your own addresses twice (Sent + Inbox copy): keep one.
    if (!view.folder || view.search) {
      const seen = new Map<string, MessageSummary>();
      for (const m of list) {
        const key = `${m.fromAddress}|${m.subject}|${m.sentDate ?? m.id}`;
        const prev = seen.get(key);
        if (!prev || (prev.direction === 'outbound' && m.direction !== 'outbound')) seen.set(key, m);
      }
      const keep = new Set([...seen.values()].map((m) => m.id));
      list = list.filter((m) => keep.has(m.id));
    }
    if (f === 'needs_reply') return list.filter((m) => m.aiCategory === 'needs_reply');
    if (f === 'important') return list.filter((m) => m.aiCategory === 'important' || m.aiCategory === 'needs_reply' || m.aiPriority === 1);
    return list;
  }, [items, view.filter, view.folder, view.search]);

  const convs = useMemo(
    () => (local.conversations && view.folder !== 'Drafts' ? groupConversations(filtered, mine) : filtered.map((m) => groupConversations([m], mine)[0])),
    [filtered, local.conversations, mine, view.folder],
  );

  // Keyboard navigation (j/k, arrows) over the visible list.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (t.isContentEditable || /INPUT|TEXTAREA|SELECT/.test(t.tagName) || e.metaKey || e.ctrlKey || e.altKey) return;
      if (!['j', 'k', 'ArrowDown', 'ArrowUp'].includes(e.key)) return;
      e.preventDefault();
      const idx = convs.findIndex((c) => c.messages.some((m) => m.id === selectedId));
      const next = e.key === 'j' || e.key === 'ArrowDown' ? Math.min(convs.length - 1, idx + 1) : Math.max(0, idx - 1);
      const c = convs[next];
      if (c) {
        select(c.latest.id);
        virtuoso.current?.scrollIntoView({ index: next, behavior: 'smooth' });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [convs, selectedId, select]);

  // Selection shortcuts: Ctrl/Cmd+A selects everything loaded, Delete/Backspace deletes the selection, Escape clears it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (t.isContentEditable || /INPUT|TEXTAREA|SELECT/.test(t.tagName) || e.altKey) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        setSelection(convs.flatMap((c) => c.messages.map((m) => m.id)));
      } else if (!e.metaKey && !e.ctrlKey && selection.length > 0 && (e.key === 'Delete' || e.key === 'Backspace')) {
        e.preventDefault();
        actions.run(selection, view.folder === 'Trash' ? 'delete' : 'trash');
        setSelection([]);
      } else if (e.key === 'Escape' && selection.length > 0) {
        setSelection([]);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [convs, selection, view.folder, actions, setSelection]);

  const title = view.search ? `“${view.search}”` : view.recipient ?? view.domain ?? (view.filter === 'needs_reply' ? 'Needs reply' : view.filter === 'flagged' ? 'Flagged' : isUnreadEverywhere(view) ? 'UnRead' : view.folder ? folderLeaf(view.folder) : 'All mail');
  const unreadHere = items.filter((m) => !m.isRead).length;
  const selectedIds = selection;
  // A plain folder view shows the folder's real totals; filtered views count what's loaded.
  const folderCount = useFolders().data?.find((f) => f.name === view.folder);
  const plainFolder = !!view.folder && !view.search && !view.recipient && !view.domain && (!view.filter || view.filter === 'all');
  const [scanOpen, setScanOpen] = useState(false);
  const allFolders = useFolders().data ?? [];
  const [deletingFolder, setDeletingFolder] = useState(false);
  /** Custom folders can be renamed or deleted from here; the system folders can't. */
  const customFolder = plainFolder && folderCount && !folderCount.isSystem ? folderCount : null;

  return (
    <div className="flex h-full min-w-0 flex-col">
      <div className="shrink-0 px-5 pb-2 pt-1">
        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0">
            <h1 className="page-title truncate text-[22px] font-semibold tracking-tight">{title}</h1>
            <div className="text-[12px] text-muted">
              {plainFolder && folderCount
                ? `${folderCount.total.toLocaleString()} email${folderCount.total === 1 ? '' : 's'} · ${folderCount.unread.toLocaleString()} unread`
                : q.isLoading ? 'Loading…' : `${filtered.length}${q.hasNextPage ? '+' : ''} messages${unreadHere ? ` · ${unreadHere} unread` : ''}`}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {(view.folder === 'Trash' || view.folder === 'Junk') && (
              <EmptyFolderButton folder={view.folder} disabled={folderCount?.total === 0} />
            )}
            {view.folder === 'Inbox' && (
              <IconButton label="Check Inbox for junk" onClick={() => setScanOpen(true)}>
                <ShieldAlert />
              </IconButton>
            )}
            {isUnreadEverywhere(view) ? (
              <MarkAllReadButton disabled={q.isLoading || filtered.length === 0} />
            ) : plainFolder && view.folder ? (
              <MarkFolderReadButton folder={view.folder} unread={folderCount?.unread ?? unreadHere} />
            ) : unreadHere > 0 ? (
              <IconButton label="Mark all read" onClick={() => actions.run(items.filter((m) => !m.isRead).map((m) => m.id), 'read')}>
                <CheckCheck />
              </IconButton>
            ) : null}
            {customFolder && (
              <>
                <IconButton
                  label="Rename folder"
                  onClick={() => void renameFolder(customFolder.name, (title, initial) => askText(title, { initial, confirmLabel: 'Rename' })).then((to) => to && (setView({ ...view, folder: to }), actions.refresh()))}
                >
                  <PencilLine />
                </IconButton>
                <IconButton label="Delete folder" onClick={() => setDeletingFolder(true)}><Trash2 /></IconButton>
              </>
            )}
            <IconButton label={local.conversations ? 'Conversation view on' : 'Conversation view off'} active={local.conversations} onClick={() => setLocal({ conversations: !local.conversations })}>
              <Layers />
            </IconButton>
            <IconButton label="Refresh" onClick={() => q.refetch()}>
              <RefreshCw className={cn(q.isFetching && 'animate-spin')} />
            </IconButton>
          </div>
          <JunkScanDialog open={scanOpen} onClose={() => setScanOpen(false)} />
          <DeleteFolderDialog
            folder={deletingFolder ? customFolder : null}
            folders={allFolders}
            onClose={() => setDeletingFolder(false)}
            onDone={() => (setDeletingFolder(false), setView({ kind: 'mail', folder: 'Inbox' }), actions.refresh())}
          />
        </div>
        {!view.search && (
          <div className="mt-3 flex gap-1.5 overflow-x-auto">
            {FILTERS.map((f) => (
              <Chip key={f.id} active={(view.filter ?? 'all') === f.id} onClick={() => setView({ ...view, filter: f.id })}>
                {(f.id === 'needs_reply' || f.id === 'important') && <Sparkles className="size-3" />}
                {f.label}
              </Chip>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {selectedIds.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -8, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -8, height: 0 }}
            className="mx-3 mb-2 flex items-center gap-1 overflow-hidden rounded-xl bg-accent/10 px-2 py-1.5"
          >
            <span className="px-2 text-[12.5px] font-semibold text-accent">{selectedIds.length} selected</span>
            <IconButton size="sm" label="Select all (Ctrl/Cmd+A)" onClick={() => setSelection(convs.flatMap((c) => c.messages.map((m) => m.id)))}><ListChecks /></IconButton>
            <div className="flex-1" />
            <IconButton size="sm" label="Archive (E)" onClick={() => (actions.run(selectedIds, 'archive'), setSelection([]))}><Archive /></IconButton>
            <IconButton size="sm" label="Mark read" onClick={() => actions.run(selectedIds, 'read')}><MailOpen /></IconButton>
            <IconButton size="sm" label="Mark unread" onClick={() => actions.run(selectedIds, 'unread')}><Mail /></IconButton>
            <IconButton size="sm" label="Flag" onClick={() => actions.run(selectedIds, 'flag')}><Flag /></IconButton>
            <IconButton size="sm" label="Junk" onClick={() => (actions.run(selectedIds, 'junk'), setSelection([]))}><ShieldAlert /></IconButton>
            <IconButton size="sm" label="Delete (Del)" onClick={() => (actions.run(selectedIds, view.folder === 'Trash' ? 'delete' : 'trash'), setSelection([]))}><Trash2 /></IconButton>
            <IconButton size="sm" label="Clear selection" onClick={() => setSelection([])}><X /></IconButton>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="min-h-0 flex-1">
        {q.isLoading ? (
          <div className="space-y-2 px-3 pt-1">
            {Array.from({ length: 9 }).map((_, i) => (
              <div key={i} className="flex gap-3 rounded-2xl p-3" style={{ opacity: 1 - i * 0.09 }}>
                <div className="skeleton size-9 shrink-0 rounded-xl" />
                <div className="flex-1 space-y-2">
                  <div className="skeleton h-3 w-1/3" />
                  <div className="skeleton h-3 w-4/5" />
                  <div className="skeleton h-3 w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : convs.length === 0 ? (
          <Empty
            icon={view.filter === 'needs_reply' ? <Sparkles /> : <Inbox />}
            title={view.filter === 'needs_reply' ? 'Nothing needs a reply' : view.search ? 'No matches' : 'All clear'}
            hint={view.filter === 'needs_reply' ? 'The AI checks new mail as it arrives and flags anything waiting on you.' : view.search ? 'Try Ask AI (Ctrl+J) for questions about your mail.' : 'New mail will show up here.'}
          />
        ) : (
          <Virtuoso
            ref={virtuoso}
            data={convs}
            computeItemKey={(_, c) => c.key}
            endReached={() => q.hasNextPage && !q.isFetchingNextPage && q.fetchNextPage()}
            increaseViewportBy={400}
            components={{ Footer: () => (q.isFetchingNextPage ? <div className="flex justify-center p-4"><Spinner /></div> : <div className="h-6" />) }}
            itemContent={(i, c) => (
              <Row
                conv={c}
                index={i}
                mine={mine}
                selected={c.messages.some((m) => m.id === selectedId)}
                checked={c.messages.some((m) => selection.includes(m.id))}
                density={local.density}
                showGist={local.showGists}
                folder={view.folder}
                onOpen={() => select(c.latest.id)}
                onCheck={(shift) => {
                  const ids = c.messages.map((m) => m.id);
                  const anchorId = selection.length ? selection[selection.length - 1] : selectedId;
                  const last = shift && anchorId ? convs.findIndex((x) => x.messages.some((m) => m.id === anchorId)) : -1;
                  if (shift && last >= 0) {
                    const [a, b] = [Math.min(last, i), Math.max(last, i)];
                    setSelection(Array.from(new Set([...selection, ...convs.slice(a, b + 1).flatMap((x) => x.messages.map((m) => m.id))])));
                  } else if (ids.every((id) => selection.includes(id))) setSelection(selection.filter((id) => !ids.includes(id)));
                  else setSelection([...selection, ...ids]);
                }}
                onAction={(action) => {
                  const ids = c.messages.map((m) => m.id);
                  if (action === 'read') actions.run(ids, c.unreadCount ? 'read' : 'unread');
                  else if (action === 'flag') actions.run([c.latest.id], c.flagged ? 'unflag' : 'flag');
                  else if (action === 'trash') actions.run(ids, view.folder === 'Trash' ? 'delete' : 'trash');
                  else actions.run(ids, 'archive');
                }}
              />
            )}
          />
        )}
      </div>
    </div>
  );
}

const Row = memo(function Row({
  conv, index, mine, selected, checked, density, showGist, folder, onOpen, onCheck, onAction,
}: {
  conv: Conversation; index: number; mine: string[]; selected: boolean; checked: boolean; density: 'cozy' | 'compact'; showGist: boolean; folder?: string;
  onOpen: () => void; onCheck: (shift: boolean) => void; onAction: (a: 'read' | 'flag' | 'trash' | 'archive') => void;
}) {
  const m = conv.latest;
  const sender = parseMailbox(m.from) ?? { address: m.fromAddress };
  const outbound = mine.includes(m.fromAddress.toLowerCase());
  const avatarFor = outbound && conv.counterparts[0] ? { name: conv.counterparts[0], address: conv.counterparts[0] } : { name: displayName(sender), address: sender.address };
  const unread = conv.unreadCount > 0;
  const cat = m.aiCategory ? CATEGORY_META[m.aiCategory] : null;
  const compact = density === 'compact';
  const gist = showGist && m.aiGist;
  const qc = useQueryClient();

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index, 12) * 0.018, duration: 0.22 }}
      className="px-2.5 py-[2px]"
    >
      <div
        role="button"
        tabIndex={-1}
        draggable
        onDragStart={(e) => {
          e.dataTransfer.setData('application/x-dts-ids', JSON.stringify(conv.messages.map((x) => x.id)));
          // The Folders header turns the sender's domain into a folder.
          e.dataTransfer.setData('application/x-dts-from', conv.messages.find((x) => x.direction !== 'outbound')?.fromAddress ?? m.fromAddress);
          e.dataTransfer.effectAllowed = 'move';
        }}
        onMouseEnter={() => qc.prefetchQuery({ queryKey: qk.message(m.id), queryFn: () => api.getMessage(m.id), staleTime: 5 * 60_000 })}
        onClick={(e) => (e.shiftKey || e.metaKey || e.ctrlKey ? onCheck(e.shiftKey) : onOpen())}
        className={cn(
          'group relative flex cursor-default gap-3 rounded-2xl transition-colors',
          compact ? 'px-3 py-2' : 'px-3 py-3',
          selected ? 'bg-accent/[0.13] shadow-[inset_0_0_0_1px] shadow-accent/25' : checked ? 'bg-accent/[0.07]' : 'hover:bg-line',
        )}
      >
        {unread && <span className="absolute left-[3px] top-1/2 h-2 w-2 -translate-y-1/2 rounded-full bg-accent shadow-[0_0_8px] shadow-accent" />}
        <button
          className="relative shrink-0 self-start"
          onClick={(e) => {
            e.stopPropagation();
            onCheck(e.shiftKey);
          }}
          title="Select"
        >
          <Avatar name={avatarFor.name} address={avatarFor.address} size={compact ? 30 : 38} ring={checked} />
          {checked && <span className="absolute -bottom-1 -right-1 flex size-4 items-center justify-center rounded-full bg-accent text-[10px] text-accent-fg">✓</span>}
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={cn('truncate text-[13.5px]', unread ? 'font-semibold text-fg' : 'font-medium text-fg/80')}>{senderLabel(conv, mine)}</span>
            {conv.messages.length > 1 && <span className="rounded-md bg-line px-1.5 text-[11px] font-semibold text-muted">{conv.messages.length}</span>}
            {folder === undefined && m.folder !== 'Inbox' && <span className="rounded-md border border-line px-1.5 text-[10.5px] text-faint">{m.folder}</span>}
            <span className="flex-1" />
            {conv.hasAttachments && <Paperclip className="size-3.5 text-faint" />}
            {conv.flagged && <Flag className="size-3.5 fill-warn text-warn" />}
            <span className={cn('shrink-0 text-[11.5px] tabular-nums group-hover:opacity-0', unread ? 'font-semibold text-accent' : 'text-faint')}><Num>{listTime(m.receivedUtc)}</Num></span>
          </div>
          <div className={cn('truncate text-[13px]', unread ? 'text-fg' : 'text-fg/70')}>{m.subject || '(no subject)'}</div>
          {!compact && (
            <div className="mt-0.5 flex items-center gap-1.5">
              {cat && (
                <span className="pill badge shrink-0 rounded-full px-1.5 py-px text-[10.5px] font-semibold" style={{ background: tint(cat.color, 11), color: cat.color }}>
                  {cat.label}
                </span>
              )}
              <span className="flex min-w-0 items-center gap-1 truncate text-[12.5px] text-muted">
                {gist && <Sparkles className="size-3 shrink-0 text-accent/80" />}
                <span className="truncate">{gist || m.snippet}</span>
              </span>
            </div>
          )}
        </div>
        <div className="absolute right-2.5 top-2 hidden items-center gap-0.5 rounded-xl border border-line bg-raised/95 p-0.5 shadow-lg backdrop-blur group-hover:flex">
          <IconButton size="sm" label="Archive" onClick={(e) => (e.stopPropagation(), onAction('archive'))}><Archive /></IconButton>
          <IconButton size="sm" label={unread ? 'Mark read' : 'Mark unread'} onClick={(e) => (e.stopPropagation(), onAction('read'))}>{unread ? <MailOpen /> : <Mail />}</IconButton>
          <IconButton size="sm" label="Flag" onClick={(e) => (e.stopPropagation(), onAction('flag'))}><Flag /></IconButton>
          <IconButton size="sm" label="Delete" onClick={(e) => (e.stopPropagation(), onAction('trash'))}><Trash2 /></IconButton>
        </div>
      </div>
    </motion.div>
  );
});

/** Permanently deletes everything in Trash or Junk, after confirming. */
function MarkAllReadButton({ disabled }: { disabled?: boolean }) {
  const actions = useMailActions();
  const [busy, setBusy] = useState(false);
  return (
    <Button
      size="sm"
      variant="outline"
      icon={<CheckCheck className="size-3.5" />}
      className="mr-1"
      disabled={disabled}
      loading={busy}
      onClick={async () => {
        setBusy(true);
        const t = toast.loading('Marking all as read…');
        try {
          const n = await actions.markAllRead((done, total) => toast.loading(`Marking all as read… ${done} of ${total}`, { id: t }));
          toast.success(n ? `Marked ${n.toLocaleString()} email${n === 1 ? '' : 's'} as read` : 'Nothing unread', { id: t });
        } catch (e) {
          toast.error(errorMessage(e), { id: t });
        } finally {
          setBusy(false);
        }
      }}
    >
      Mark All As Read
    </Button>
  );
}

/** Marks every unread email in a folder as read, including the part of a long folder that isn't loaded yet. */
function MarkFolderReadButton({ folder, unread }: { folder: string; unread: number }) {
  const actions = useMailActions();
  const [busy, setBusy] = useState(false);
  return (
    <IconButton
      label={`Mark all read${unread ? ` (${unread.toLocaleString()})` : ''}`}
      disabled={busy || unread === 0}
      onClick={async () => {
        setBusy(true);
        const t = toast.loading('Marking all as read…');
        try {
          const n = await actions.markFolderRead(folder, (done, total) => toast.loading(`Marking all as read… ${done} of ${total}`, { id: t }));
          toast.success(n ? `Marked ${n.toLocaleString()} email${n === 1 ? '' : 's'} as read` : 'Nothing unread', { id: t });
        } catch (e) {
          toast.error(errorMessage(e), { id: t });
        } finally {
          setBusy(false);
        }
      }}
    >
      <CheckCheck className={cn(busy && 'animate-pulse')} />
    </IconButton>
  );
}

function EmptyFolderButton({ folder, disabled }: { folder: 'Trash' | 'Junk'; disabled?: boolean }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  return (
    <Button
      size="sm"
      variant="outline"
      icon={<Trash2 className="size-3.5" />}
      className="mr-1 hover:border-danger/60 hover:text-danger"
      disabled={disabled}
      loading={busy}
      onClick={async () => {
        if (!(await confirmDialog(`Empty ${folder}?`, 'Messages are deleted permanently.', { confirmLabel: 'Empty', danger: true }))) return;
        setBusy(true);
        try {
          const r = await api.emptyFolder(folder);
          toast.success(`Deleted ${r.deleted} message${r.deleted === 1 ? '' : 's'}`);
          void qc.invalidateQueries();
        } catch (e) {
          toast.error(errorMessage(e));
        } finally {
          setBusy(false);
        }
      }}
    >
      Empty {folder}
    </Button>
  );
}
